import { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertTriangle, Inbox, HelpCircle, VideoOff } from 'lucide-react';
import fetchWithTenant from '../../utils/fetchWithTenant';

// 868jryv9g [6.8]: investigation-level "Related events" panel. Reads the
// investigation's selected cameras (deviceSelections.cameras, flat id array that
// post-868jyxe4c can carry discovery cuids equal to AnalyticsEvent.cameraId) plus
// its centered incident window, then lists analytics events on those cameras in
// that window. Self-contained: the analytics query endpoint takes ONE cameraId,
// so we fan out per camera and merge client-side. We deliberately do NOT reuse
// AnalyticsEventList (hard-bound to the global analyticsStore); we replicate its
// column layout and timestamp formatting for a visual match.
//
// Never False Green: a missing window or empty camera set is a DISTINCT visible
// state, not an empty list; per-camera fetch failures surface a partial banner
// above whatever loaded rather than reading as "no events".

const PER_CAMERA_LIMIT = 200;
const DEFAULT_WINDOW_MINUTES = 15;

// Mirrors AnalyticsEventList.formatTimestamp (that helper is not exported).
function formatTimestamp(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

// Renders a single expanded-detail value. Objects/arrays (data, elements) are
// pretty-printed; primitives are stringified; null/undefined collapse to a dash.
function DetailValue({ value }) {
  if (value === null || value === undefined || value === '') return <span>-</span>;
  if (typeof value === 'object') {
    return <pre className="inv-rae-detail-json">{JSON.stringify(value, null, 2)}</pre>;
  }
  return <span>{String(value)}</span>;
}

function DetailField({ label, value }) {
  return (
    <div className="inv-rae-detail-field">
      <span className="inv-rae-detail-label">{label}</span>
      <DetailValue value={value} />
    </div>
  );
}

export default function RelatedAnalyticsEvents({ caseData }) {
  const cameras = useMemo(() => {
    const c = caseData?.deviceSelections?.cameras;
    return Array.isArray(c) ? c : [];
  }, [caseData]);

  const incidentTimestamp = caseData?.incidentTimestamp;
  const windowMinutes = caseData?.windowMinutes || DEFAULT_WINDOW_MINUTES;

  // Centered window, reusing the evidence gatherer's derivation exactly
  // (incidentMs +/- windowMinutes). null when there is no usable incident time.
  const timeWindow = useMemo(() => {
    if (!incidentTimestamp) return null;
    const incidentMs = new Date(incidentTimestamp).getTime();
    if (Number.isNaN(incidentMs)) return null;
    const from = new Date(incidentMs - windowMinutes * 60000).toISOString();
    const to = new Date(incidentMs + windowMinutes * 60000).toISOString();
    return { from, to };
  }, [incidentTimestamp, windowMinutes]);

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [nameMap, setNameMap] = useState(() => new Map());
  const [errorCount, setErrorCount] = useState(0);
  const [anyHasMore, setAnyHasMore] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!timeWindow || cameras.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setExpandedId(null);

    (async () => {
      // Camera names are best-effort; an id that does not resolve still renders
      // (as the raw id), never silently dropped.
      const names = new Map();
      try {
        const r = await fetchWithTenant('/api/md/devices?deviceType=CAMERA');
        if (r.ok) {
          const data = await r.json();
          for (const d of data.devices || []) {
            names.set(d.id, d.name || d.externalName || d.hostname || d.ipAddress || d.id);
          }
        }
      } catch {
        // ignore: name resolution is non-critical
      }

      const { from, to } = timeWindow;
      const results = await Promise.allSettled(
        cameras.map((cid) =>
          fetchWithTenant(
            `/api/analytics/events?cameraId=${encodeURIComponent(cid)}` +
              `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` +
              `&limit=${PER_CAMERA_LIMIT}&sort=desc`
          ).then(async (res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
          })
        )
      );

      if (cancelled) return;

      const merged = [];
      let errored = 0;
      let anyMore = false;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          merged.push(...(r.value?.events || []));
          if (r.value?.hasMore) anyMore = true;
        } else {
          errored += 1;
        }
      }
      // Client-side merge sort by receivedAt desc across all cameras.
      merged.sort(
        (a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
      );

      setNameMap(names);
      setEvents(merged);
      setErrorCount(errored);
      setAnyHasMore(anyMore);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [timeWindow, cameras]);

  // ── Never-False-Green guard states (each distinct and visible) ──
  if (!timeWindow) {
    return (
      <div className="inv-rae-panel">
        <div className="inv-rae-state inv-rae-unknown">
          <HelpCircle size={20} />
          <div className="inv-rae-state-title">Time window unknown</div>
          <div className="inv-rae-state-sub">
            Cannot determine a time window for this investigation.
          </div>
        </div>
      </div>
    );
  }

  if (cameras.length === 0) {
    return (
      <div className="inv-rae-panel">
        <div className="inv-rae-state">
          <VideoOff size={20} />
          <div className="inv-rae-state-title">No cameras selected</div>
          <div className="inv-rae-state-sub">
            No cameras selected for this investigation.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inv-rae-panel">
      {errorCount > 0 && (
        <div className="inv-rae-banner">
          <AlertTriangle size={15} />
          <span>
            Could not load events for {errorCount} camera{errorCount !== 1 ? 's' : ''}. Showing
            partial results.
          </span>
        </div>
      )}

      {loading ? (
        <div className="inv-rae-loading">
          <Loader2 size={20} className="spin" />
          <span>Loading analytics events…</span>
        </div>
      ) : events.length === 0 && errorCount === 0 ? (
        <div className="inv-rae-state">
          <Inbox size={20} />
          <div className="inv-rae-state-title">No analytics events</div>
          <div className="inv-rae-state-sub">
            No analytics events on the selected cameras in this window.
          </div>
        </div>
      ) : events.length === 0 ? (
        // All loaded cameras errored: banner already shown above, keep the list
        // region from collapsing into a misleading empty success state.
        <div className="inv-rae-state">
          <AlertTriangle size={20} />
          <div className="inv-rae-state-title">Events unavailable</div>
          <div className="inv-rae-state-sub">
            Events could not be loaded for the selected cameras.
          </div>
        </div>
      ) : (
        <>
          <div className="inv-rae-list">
            <div className="inv-rae-row inv-rae-header">
              <span>Received</span>
              <span>Camera</span>
              <span>Topic</span>
              <span>Vendor</span>
              <span>Category</span>
              <span>Value</span>
            </div>

            {events.map((ev) => {
              const expanded = ev.id === expandedId;
              return (
                <div key={ev.id} className="inv-rae-item">
                  <div
                    className={`inv-rae-row inv-rae-data-row${expanded ? ' expanded' : ''}`}
                    onClick={() => setExpandedId(expanded ? null : ev.id)}
                  >
                    <span className="inv-rae-cell-time">{formatTimestamp(ev.receivedAt)}</span>
                    <span className="inv-rae-cell-mono">
                      {nameMap.get(ev.cameraId) || ev.cameraId || '-'}
                    </span>
                    <span className="inv-rae-cell-mono">
                      {ev.canonicalTopic || ev.vendorTopic || '-'}
                    </span>
                    <span>{ev.vendor || '-'}</span>
                    <span>{ev.eventCategory || '-'}</span>
                    <span>{ev.sourceValue ?? ev.dataValue ?? '-'}</span>
                  </div>

                  {expanded && (
                    <div className="inv-rae-expand">
                      <DetailField label="Property operation" value={ev.propertyOperation} />
                      <DetailField label="Source key" value={ev.sourceKey} />
                      <DetailField label="Source value" value={ev.sourceValue} />
                      <DetailField label="Zone name" value={ev.zoneName} />
                      <DetailField label="Camera reported at" value={ev.cameraReportedAt} />
                      <DetailField label="Clock skew (ms)" value={ev.clockSkewMs} />
                      <DetailField label="Data" value={ev.data} />
                      <DetailField label="Elements" value={ev.elements} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {anyHasMore && (
            <div className="inv-rae-footnote">
              Some cameras have more events in this window.
            </div>
          )}
        </>
      )}
    </div>
  );
}
